import { useGraphqlQuery } from "graphql-mock"
import type { SetQueryParam } from "hooks"
import { times } from "lodash-es"
import { useErrorSentryLog } from "materia"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import type { ProductTypeIdentifier } from "../../../graphql"
import { COURSE_PRODUCT_TYPES, REAL_TALK_PRODUCT_TYPES } from "../ExploreView"
import { query } from "./gql"

import s from "./ExploreFilters.module.scss"

type ExploreFiltersProps = {
  selectedProductType: ProductTypeIdentifier | undefined
  setSelectedProductType: SetQueryParam<ProductTypeIdentifier>
}
export const ExploreFiltersConnected = ({
  selectedProductType,
  setSelectedProductType,
}: ExploreFiltersProps) => {
  const { data, error, isPending } = useGraphqlQuery(query, {})

  if (isPending) return <ExploreFiltersSkeleton />
  if (error) return <ExploreFiltersError error={error} />

  const productTypes = data?.productTypes ?? []

  return (
    <ExploreFilters
      selectedProductType={selectedProductType}
      setSelectedProductType={setSelectedProductType}
      productTypes={productTypes}
    />
  )
}

export const ExploreFilters = ({
  selectedProductType,
  setSelectedProductType,
  productTypes,
}: {
  selectedProductType: ProductTypeIdentifier | undefined
  setSelectedProductType: SetQueryParam<ProductTypeIdentifier>
  productTypes: ProductTypeIdentifier[]
}) => {
  const { t } = useTranslation()

  // [TODO] https://jira.attensi.com/browse/WEB-21038
  // We exclude real_talk and course (scorm/xapi) types because they're a bit
  // different - upon selecting, they should show all product types in their
  // group. This logic is handled separately below
  const ungroupedProductTypes = productTypes.filter(
    (type) =>
      !REAL_TALK_PRODUCT_TYPES.includes(type) &&
      !COURSE_PRODUCT_TYPES.includes(type)
  )

  const hasRealTalkProducts =
    productTypes.filter((type) => REAL_TALK_PRODUCT_TYPES.includes(type))
      .length > 0

  const hasCourseProducts =
    productTypes.filter((type) => COURSE_PRODUCT_TYPES.includes(type)).length >
    0

  const handleFilteByProductType = (type: ProductTypeIdentifier) => {
    const newActiveType = (selectedProductType ?? "").includes(type)
      ? undefined
      : type
    setSelectedProductType(newActiveType)
  }

  return (
    // biome-ignore lint/a11y/useSemanticElements: a toggle-button group, not grouped form controls -- fieldset would want a legend and bring its own box styling
    <div className={s.exploreFilters} role="group">
      {ungroupedProductTypes.map((type) => (
        <ExploreFilter
          key={type}
          isActive={selectedProductType === type}
          onClick={() => handleFilteByProductType(type)}
          text={t(`product.PRODUCT_TYPES.${type}`)}
        />
      ))}

      {hasCourseProducts && (
        <ExploreFilter
          key="course_products"
          isActive={selectedProductType === "scorm"}
          onClick={() => handleFilteByProductType("scorm")}
          text={t("product.PRODUCT_TYPES.scorm")}
        />
      )}

      {hasRealTalkProducts && (
        <ExploreFilter
          key="real_talk_products"
          isActive={selectedProductType === "real_talk"}
          onClick={() => handleFilteByProductType("real_talk")}
          text={t("product.PRODUCT_TYPES.real_talk")}
        />
      )}
    </div>
  )
}

const ExploreFilter = ({
  isActive,
  onClick,
  text,
}: {
  isActive: boolean
  onClick: () => void
  text: string
}) => {
  return (
    <button
      type="button"
      className={modifiers(s, "exploreFilter", { isActive })}
      onClick={onClick}
    >
      {text}
    </button>
  )
}

export const ExploreFiltersSkeleton = () => {
  return (
    <div className={s.exploreFilters}>
      {times(5, (index) => (
        <button type="button" className={s.exploreFilterSkeleton} key={index}>
          &nbsp;
        </button>
      ))}
    </div>
  )
}

export const ExploreFiltersError = ({ error }: { error: unknown }) => {
  // TODO [sentry-logging] https://jira.attensi.com/browse/WEB-18745
  // Find a better place to send Sentry logs from
  useErrorSentryLog(error)
  const { t } = useTranslation()

  return (
    <div className={s.exploreFilters}>
      <p className={s.exploreFilters__error}>
        {t("error.FILTERS_FETCH_ERROR")}
      </p>
    </div>
  )
}
